import type { EnvVar } from '@/api/schemas';
import { FormField } from '@/components/FormField';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Tag, VisibilityBadge } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { ValueEditor } from './ValueEditor';
import { useNewVersionForm } from './useNewVersionForm';

/** New immutable version of a variable (`POST /env-vars/{id}/versions`) with `expectedCurrentVersion` as the concurrency guard. */
export function NewVersionSheet({ envVar, onOpenChange }: { envVar: EnvVar | null; onOpenChange: (o: boolean) => void }) {
  return (
    <Sheet open={Boolean(envVar)} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="env-version-sheet" className="w-full gap-0 p-0" aria-describedby="env-version-desc">
        {envVar ? <VersionForm key={envVar.id} envVar={envVar} onDone={() => onOpenChange(false)} /> : null}
      </SheetContent>
    </Sheet>
  );
}

function VersionForm({ envVar, onDone }: { envVar: EnvVar; onDone: () => void }) {
  const f = useNewVersionForm(envVar, onDone);
  return (
    <form data-testid="env-version-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex h-full min-h-0 flex-col" aria-label="New version">
      <SheetHeader>
        <SheetTitle className="flex flex-wrap items-center gap-2">
          <span className="font-mono">{envVar.key}</span>
          <VisibilityBadge visibility={envVar.visibility} />
          {f.current ? <Tag>current v{f.current}</Tag> : null}
        </SheetTitle>
        <SheetDescription id="env-version-desc" className="text-xs">
          Creates v{(f.current ?? 0) + 1}; the previous versions stay readable by the snapshots that reference them.
        </SheetDescription>
      </SheetHeader>
      <VersionBody envVar={envVar} f={f} />
      <SheetFooter className="flex-row justify-end border-t">
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" data-testid="env-version-submit" loading={f.isPending}>
          Save new version
        </Button>
      </SheetFooter>
    </form>
  );
}

function VersionBody({ envVar, f }: { envVar: EnvVar; f: ReturnType<typeof useNewVersionForm> }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5">
      {f.valueType !== envVar.valueType ? <p className="text-xs text-awaiting">The type becomes multiline after the conversion — save the metadata change from “Edit metadata” as well.</p> : null}
      <ValueEditor id="env-version-value" valueType={f.valueType} state={f.value} onChange={f.setValue} onValueTypeChange={f.setValueType} required submitted={f.submitted} />
      <FormField id="env-version-note" label="Note" hint="Why the value changed (audit trail)">
        <Textarea data-testid="env-version-note" value={f.note} onChange={(e) => f.setNote(e.target.value)} rows={2} maxLength={500} placeholder="Point staging at API v2 (ST-612)" />
      </FormField>
      <ProblemAlert error={f.problem} testId="env-version-problem" />
    </div>
  );
}
